import React, { useEffect, useState, useCallback } from "react";
import { useCompany } from "../../context/CompanyContext";
import {
  createCalendar,
  createPeriod,
  getCalendars,
  getPeriods,
} from "../payrollService";
import PayrollTableEmpty from "../components/PayrollTableEmpty";

export default function PayrollCalendars() {
  const { companyId } = useCompany();
  const [calendars, setCalendars] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [msg, setMsg] = useState("");

  const [calendarForm, setCalendarForm] = useState({
    calendar_name: "",
    pay_frequency: "monthly",
    payment_day: "",
    description: "",
  });

  const [periodForm, setPeriodForm] = useState({
    calendar_id: "",
    period_name: "",
    period_start: "",
    period_end: "",
    payment_date: "",
  });

  const load = useCallback(async () => {
    if (!companyId) return;

    try {
      const [calendarData, periodData] = await Promise.all([
        getCalendars(companyId),
        getPeriods(companyId),
      ]);

      setCalendars(calendarData || []);
      setPeriods(periodData || []);
    } catch (err) {
      setMsg(err.message || "Failed to load calendars and periods");
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  async function saveCalendar(e) {
    e.preventDefault();
    try {
      await createCalendar({
        company_id: companyId,
        calendar_name: calendarForm.calendar_name,
        pay_frequency: calendarForm.pay_frequency,
        payment_day: calendarForm.payment_day
          ? Number(calendarForm.payment_day)
          : null,
        description: calendarForm.description,
      });
      setCalendarForm({
        calendar_name: "",
        pay_frequency: "monthly",
        payment_day: "",
        description: "",
      });
      load();
    } catch (err) {
      setMsg(err.message || "Failed to create calendar");
    }
  }

  async function savePeriod(e) {
    e.preventDefault();
    try {
      await createPeriod({
        company_id: companyId,
        calendar_id: periodForm.calendar_id,
        period_name: periodForm.period_name,
        period_start: periodForm.period_start,
        period_end: periodForm.period_end,
        payment_date: periodForm.payment_date,
      });
      setPeriodForm({
        calendar_id: "",
        period_name: "",
        period_start: "",
        period_end: "",
        payment_date: "",
      });
      load();
    } catch (err) {
      setMsg(err.message || "Failed to create period");
    }
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert error">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-2">
        <div className="payroll-card">
          <div className="payroll-card-header">
            <h3>Create Payroll Calendar</h3>
          </div>
          <form className="payroll-form" onSubmit={saveCalendar}>
            <label>
              Calendar Name
              <input
                value={calendarForm.calendar_name}
                onChange={(e) =>
                  setCalendarForm((prev) => ({
                    ...prev,
                    calendar_name: e.target.value,
                  }))
                }
                required
              />
            </label>
            <label>
              Pay Frequency
              <select
                value={calendarForm.pay_frequency}
                onChange={(e) =>
                  setCalendarForm((prev) => ({
                    ...prev,
                    pay_frequency: e.target.value,
                  }))
                }
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="semi_monthly">Semi Monthly</option>
                <option value="monthly">Monthly</option>
              </select>
            </label>
            <label>
              Payment Day
              <input
                type="number"
                value={calendarForm.payment_day}
                onChange={(e) =>
                  setCalendarForm((prev) => ({
                    ...prev,
                    payment_day: e.target.value,
                  }))
                }
              />
            </label>
            <label>
              Description
              <textarea
                rows="3"
                value={calendarForm.description}
                onChange={(e) =>
                  setCalendarForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </label>
            <button type="submit" className="primary-btn">
              Save Calendar
            </button>
          </form>
        </div>

        <div className="payroll-card">
          <div className="payroll-card-header">
            <h3>Create Payroll Period</h3>
          </div>
          <form className="payroll-form" onSubmit={savePeriod}>
            <label>
              Calendar
              <select
                value={periodForm.calendar_id}
                onChange={(e) =>
                  setPeriodForm((prev) => ({
                    ...prev,
                    calendar_id: e.target.value,
                  }))
                }
                required
              >
                <option value="">Select calendar</option>
                {calendars.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.calendar_name} ({c.pay_frequency})
                  </option>
                ))}
              </select>
            </label>
            <label>
              Period Name
              <input
                value={periodForm.period_name}
                onChange={(e) =>
                  setPeriodForm((prev) => ({
                    ...prev,
                    period_name: e.target.value,
                  }))
                }
                required
              />
            </label>
            <div className="payroll-grid payroll-grid-3">
              <label>
                Period Start
                <input
                  type="date"
                  value={periodForm.period_start}
                  onChange={(e) =>
                    setPeriodForm((prev) => ({
                      ...prev,
                      period_start: e.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label>
                Period End
                <input
                  type="date"
                  value={periodForm.period_end}
                  onChange={(e) =>
                    setPeriodForm((prev) => ({
                      ...prev,
                      period_end: e.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label>
                Payment Date
                <input
                  type="date"
                  value={periodForm.payment_date}
                  onChange={(e) =>
                    setPeriodForm((prev) => ({
                      ...prev,
                      payment_date: e.target.value,
                    }))
                  }
                  required
                />
              </label>
            </div>
            <button type="submit" className="primary-btn">
              Save Period
            </button>
          </form>
        </div>
      </div>

      <div className="payroll-grid payroll-grid-2">
        <div className="payroll-card">
          <div className="payroll-card-header">
            <h3>Calendars</h3>
          </div>
          {!calendars.length ? (
            <PayrollTableEmpty title="No calendars yet" />
          ) : (
            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Frequency</th>
                    <th>Payment Day</th>
                  </tr>
                </thead>
                <tbody>
                  {calendars.map((row) => (
                    <tr key={row.id}>
                      <td>{row.calendar_name}</td>
                      <td>{row.pay_frequency}</td>
                      <td>{row.payment_day || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="payroll-card">
          <div className="payroll-card-header">
            <h3>Periods</h3>
          </div>
          {!periods.length ? (
            <PayrollTableEmpty title="No periods yet" />
          ) : (
            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Calendar</th>
                    <th>Status</th>
                    <th>Payment Date</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.map((row) => {
                    const cal = Array.isArray(row.payroll_calendars)
                      ? row.payroll_calendars[0]
                      : row.payroll_calendars;
                    return (
                      <tr key={row.id}>
                        <td>{row.period_name}</td>
                        <td>{cal?.calendar_name || "-"}</td>
                        <td>
                          <span className={`pill ${row.status}`}>
                            {row.status}
                          </span>
                        </td>
                        <td>{row.payment_date}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
